import cn from 'classnames';
import parse from 'html-react-parser';
import { FC } from 'react';

import {
  cleanBuilderParagraphInput,
  cleanBuilderStringInput,
  getBuilderString,
} from '@/shared/text';

import { Action, ActionVariant } from '../Action';
import style from './Content.module.scss';
import { ScrollDownArrow } from './ScrollDownArrow';

export enum ContentType {
  STANDARD = 'standard',
  CONTENT = 'content',
  ACTIVATIONS = 'activations',
  COLLECTIONS = 'collections',
}

function shouldShowSubHeadline(type: ContentType, subHeadline?: string) {
  if (![ContentType.ACTIVATIONS, ContentType.COLLECTIONS].includes(type)) {
    return false;
  }
  if (!subHeadline) return false;
  return true;
}

interface Props {
  locale: string;
  type: ContentType;
  headline?: string;
  subHeadline?: string;
  description?: string;
  linkToId?: string;
  action: Action;
}
export const Content: FC<Props> = ({
  locale,
  type,
  headline,
  subHeadline,
  description,
  linkToId,
  action,
}) => (
  <div
    data-testid="hero-content"
    className={cn(
      'tw:flex',
      'tw:flex-1',
      'tw:flex-col',
      'tw:justify-center',
      style.content,
      {
        [style.limitWidth]: [
          ContentType.CONTENT,
          ContentType.COLLECTIONS,
        ].includes(type),
      }
    )}
  >
    <div
      className={cn(
        'tw:flex',
        'tw:flex-1',
        'tw:flex-col',
        'tw:justify-center',
        style.content,
        {
          [style.limitWidth]: [
            ContentType.CONTENT,
            ContentType.COLLECTIONS,
          ].includes(type),
        }
      )}
    >
      <div className={style.text}>
        <div
          data-testid="headline"
          className={cn('u-typography-h1', {
            'u-m-b-xl':
              shouldShowSubHeadline(
                type,
                getBuilderString(subHeadline, locale)
              ) ||
              getBuilderString(description, locale) ||
              (action.variant && action.variant !== ActionVariant.NONE),
          })}
        >
          {parse(
            cleanBuilderParagraphInput(
              getBuilderString(headline, locale) || 'Headline'
            )
          )}
        </div>
        {shouldShowSubHeadline(type, getBuilderString(subHeadline, locale)) &&
          getBuilderString(subHeadline, locale) && (
            <div
              data-testid="subHeadline"
              className={cn({
                'u-typography-h2': type !== ContentType.ACTIVATIONS,
                'u-typography-h3': type === ContentType.ACTIVATIONS,
                'u-m-b-xl':
                  description ||
                  (action.variant && action.variant !== ActionVariant.NONE),
              })}
            >
              {parse(
                cleanBuilderStringInput(getBuilderString(subHeadline, locale))
              )}
            </div>
          )}
        {description && (
          <div
            data-testid="description"
            className={cn({
              'u-typography-h4': [
                ContentType.CONTENT,
                ContentType.STANDARD,
              ].includes(type),
              'u-typography-h3': type === ContentType.COLLECTIONS,
              'u-typography-h6': type === ContentType.ACTIVATIONS,
              'u-m-b-xl':
                action.variant && action.variant !== ActionVariant.NONE,
            })}
          >
            {parse(
              cleanBuilderParagraphInput(getBuilderString(description, locale))
            )}
          </div>
        )}
        <Action {...action} />
      </div>
    </div>
    {type === ContentType.STANDARD && linkToId && (
      <ScrollDownArrow linkToId={linkToId} />
    )}
  </div>
);
